// =============================================================================
// ANCHOR PROGRAM CLIENT
// Interfaces with deployed Anchor programs via Solana transactions
// =============================================================================

import { Connection, PublicKey, Transaction, TransactionInstruction, SystemProgram, LAMPORTS_PER_SOL, SYSVAR_RENT_PUBKEY } from "@solana/web3.js";
import { Buffer } from "buffer";
import { Program, AnchorProvider, BN } from "@coral-xyz/anchor";
import { TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddress } from "@solana/spl-token";
import MoonrunBondingCurveIDL from "../../idl/moonrun_bonding_curve.json";
import MoonrunTokenIDL from "../../idl/moonrun_token.json";
import MoonrunGovernanceIDL from "../../idl/moonrun_governance.json";
import {
  PROGRAM_IDS,
  findTokenConfigPDA,
  findPoolPDA,
  findSellQueuePDA,
  findGovernancePDA,
  findVotePDA,
  findPoolTokenAccountPDA,
  findPoolSolAccountPDA,
  BONDING_CURVE_CONSTANTS,
} from "./idl-types.ts";

// =============================================================================
// EVENT TYPES
// =============================================================================

export interface TradeEventData {
  pool: PublicKey;
  trader: PublicKey;
  isBuy: boolean;
  solAmount: BN;
  tokenAmount: BN;
  newSolReserve: BN;
  newTokenReserve: BN;
  marketCap: BN;
  timestamp: BN;
}

export interface LockEventData {
  pool: PublicKey;
  locker: PublicKey;
  timestamp: BN;
}

export interface MigrationEventData {
  pool: PublicKey;
  migrator: PublicKey;
  timestamp: BN;
}

export interface VoteEventData {
  governance: PublicKey;
  voter: PublicKey;
  direction: boolean;
  timestamp: BN;
}

export interface VoteResultEventData {
  governance: PublicKey;
  shouldUnlock: boolean;
  timestamp: BN;
}

// =============================================================================
// CONFIGURATION
// =============================================================================

export interface AnchorClientConfig {
  connection: Connection;
  wallet: {
    publicKey: PublicKey;
    signTransaction: (tx: Transaction) => Promise<Transaction>;
    signAllTransactions: (txs: Transaction[]) => Promise<Transaction[]>;
  };
}

// =============================================================================
// TOKEN PROGRAM CLIENT
// =============================================================================

export class TokenProgramClient {
  private connection: Connection;
  private programId: PublicKey;
  private program: Program;

  constructor(provider: AnchorProvider, programId?: string) {
    this.connection = provider.connection;
    this.programId = new PublicKey(programId || PROGRAM_IDS.TOKEN);
    this.program = new Program(MoonrunTokenIDL as any, this.programId, provider);
  }

  async getTokenConfigData(mint: PublicKey) {
    const [pda] = findTokenConfigPDA(mint, this.programId);
    try {
      return await this.program.account.tokenConfig.fetch(pda);
    } catch {
      return null;
    }
  }

  async getTokenConfig(mint: PublicKey) {
    return await this.getTokenConfigData(mint);
  }

  decodeTokenConfigData(data: Buffer) {
    return this.program.coder.accounts.decode('tokenConfig', data);
  }

  // Returns instruction to create token (to be signed by wallet)
  async buildCreateTokenInstruction(
    creator: PublicKey,
    mint: PublicKey,
    name: string,
    symbol: string,
    uri: string,
    decimals: number = 9,
    totalSupply: bigint = 1_000_000_000_000_000_000n
  ): Promise<TransactionInstruction> {
    const [tokenConfig] = findTokenConfigPDA(mint, this.programId);

    return await this.program.methods
      .createToken(name, symbol, uri, decimals, new BN(totalSupply.toString()))
      .accounts({
        creator,
        mint,
        tokenConfig,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
        rent: SYSVAR_RENT_PUBKEY,
      })
      .instruction();
  }

  // Returns instruction to mint initial supply to bonding curve pool
  async buildMintInitialSupplyInstruction(
    creator: PublicKey,
    mint: PublicKey,
    poolTokenAccount: PublicKey,
    initialSupply: bigint
  ): Promise<TransactionInstruction> {
    const [tokenConfig] = findTokenConfigPDA(mint, this.programId);

    return await this.program.methods
      .mintInitialSupply(new BN(initialSupply.toString()))
      .accounts({
        creator,
        mint,
        tokenConfig,
        poolTokenAccount,
        tokenProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();
  }

  // Returns instruction to freeze mint authority
  async buildFreezeMintInstruction(
    creator: PublicKey,
    mint: PublicKey
  ): Promise<TransactionInstruction> {
    const [tokenConfig] = findTokenConfigPDA(mint, this.programId);

    return await this.program.methods
      .freezeMintAuthority()
      .accounts({
        creator,
        mint,
        tokenConfig,
        tokenProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();
  }
}

// =============================================================================
// BONDING CURVE PROGRAM CLIENT
// =============================================================================

export class BondingCurveProgramClient {
  private connection: Connection;
  private programId: PublicKey;
  private program: Program;

  constructor(provider: AnchorProvider, programId?: string) {
    this.connection = provider.connection;
    this.programId = new PublicKey(programId || PROGRAM_IDS.BONDING_CURVE);
    this.program = new Program(MoonrunBondingCurveIDL as any, this.programId, provider);
  }

  async getPool(tokenMint: PublicKey) {
    const [pda] = findPoolPDA(tokenMint, this.programId);
    const accountInfo = await this.connection.getAccountInfo(pda);
    if (!accountInfo) return null;
    return accountInfo;
  }

  async getPoolData(tokenMint: PublicKey) {
    const [pda] = findPoolPDA(tokenMint, this.programId);
    try {
      return await this.program.account.bondingCurvePool.fetch(pda);
    } catch {
      return null;
    }
  }

  decodePoolAccount(data: Buffer) {
    return this.program.coder.accounts.decode('bondingCurvePool', data);
  }

  async getSellQueue(pool: PublicKey) {
    const [pda] = findSellQueuePDA(pool, this.programId);
    const accountInfo = await this.connection.getAccountInfo(pda);
    if (!accountInfo) return null;
    return accountInfo;
  }

  // Calculate expected output for a buy
  calculateBuyOutput(solReserve: number, tokenReserve: number, solAmount: number) {
    const k = solReserve * tokenReserve;
    const newSolReserve = solReserve + solAmount;
    const newTokenReserve = k / newSolReserve;
    const tokensOut = tokenReserve - newTokenReserve;
    
    // Apply fee
    const fee = solAmount * (BONDING_CURVE_CONSTANTS.FEE_BASIS_POINTS / 10000);
    const effectiveTokensOut = tokensOut * (1 - BONDING_CURVE_CONSTANTS.FEE_BASIS_POINTS / 10000);
    
    return {
      tokensOut: effectiveTokensOut,
      fee,
      priceImpact: ((newSolReserve / newTokenReserve) / (solReserve / tokenReserve) - 1) * 100,
    };
  }

  // Calculate expected output for a sell
  calculateSellOutput(solReserve: number, tokenReserve: number, tokenAmount: number) {
    const k = solReserve * tokenReserve;
    const newTokenReserve = tokenReserve + tokenAmount;
    const newSolReserve = k / newTokenReserve;
    const solOut = solReserve - newSolReserve;
    
    // Apply fee
    const fee = solOut * (BONDING_CURVE_CONSTANTS.FEE_BASIS_POINTS / 10000);
    const effectiveSolOut = solOut - fee;
    
    return {
      solOut: effectiveSolOut,
      fee,
      priceImpact: (1 - (newSolReserve / newTokenReserve) / (solReserve / tokenReserve)) * 100,
    };
  }

  async buildBuyInstruction(
    buyer: PublicKey,
    tokenMint: PublicKey,
    solAmount: bigint,
    minTokensOut: bigint
  ) {
    const [poolPda] = findPoolPDA(tokenMint, this.programId);
    const [poolTokenAccount] = findPoolTokenAccountPDA(poolPda, this.programId);
    const [poolSolAccount] = findPoolSolAccountPDA(poolPda, this.programId);
    const buyerTokenAccount = await getAssociatedTokenAddress(tokenMint, buyer);
    const [feeRecipient] = findGovernancePDA(tokenMint, new PublicKey(PROGRAM_IDS.GOVERNANCE));

    const instruction = await this.program.methods
      .buy(solAmount, minTokensOut)
      .accounts({
        buyer,
        pool: poolPda,
        poolTokenAccount,
        poolSolAccount,
        buyerTokenAccount,
        tokenMint,
        feeRecipient,
        tokenProgram: TOKEN_PROGRAM_ID,
        associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
        rent: SYSVAR_RENT_PUBKEY,
        systemProgram: SystemProgram.programId,
      })
      .instruction();

    return instruction;
  }

  async buildSellInstruction(
    seller: PublicKey,
    tokenMint: PublicKey,
    tokenAmount: bigint,
    minSolOut: bigint
  ) {
    const [poolPda] = findPoolPDA(tokenMint, this.programId);
    const [poolTokenAccount] = findPoolTokenAccountPDA(poolPda, this.programId);
    const [poolSolAccount] = findPoolSolAccountPDA(poolPda, this.programId);
    const sellerTokenAccount = await getAssociatedTokenAddress(tokenMint, seller);
    const [feeRecipient] = findGovernancePDA(tokenMint, new PublicKey(PROGRAM_IDS.GOVERNANCE));

    const instruction = await this.program.methods
      .sell(tokenAmount, minSolOut)
      .accounts({
        seller,
        pool: poolPda,
        poolTokenAccount,
        poolSolAccount,
        sellerTokenAccount,
        tokenMint,
        feeRecipient,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      })
      .instruction();

    return instruction;
  }

  async buildInitializePoolInstruction(
    creator: PublicKey,
    tokenMint: PublicKey,
    targetMarketCap: bigint
  ): Promise<TransactionInstruction> {
    const [pool] = findPoolPDA(tokenMint, this.programId);
    const poolTokenAccount = await getAssociatedTokenAddress(tokenMint, pool, true);
    const [poolSolAccount] = findPoolSolAccountPDA(pool, this.programId);

    return await this.program.methods
      .initializePool(new BN(targetMarketCap.toString()))
      .accounts({
        creator,
        tokenMint,
        pool,
        poolTokenAccount,
        poolSolAccount,
        tokenProgram: TOKEN_PROGRAM_ID,
        associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
        rent: SYSVAR_RENT_PUBKEY,
      })
      .instruction();
  }

  buildQueueSellInstruction(
    seller: PublicKey,
    tokenMint: PublicKey,
    tokenAmount: bigint,
    minSolOut: bigint,
    isPriority: boolean
  ) {
    const [poolPda] = findPoolPDA(tokenMint, this.programId);
    const [sellQueuePda] = findSellQueuePDA(poolPda, this.programId);
    
    return {
      programId: this.programId,
      keys: [
        { pubkey: seller, isSigner: true, isWritable: true },
        { pubkey: poolPda, isSigner: false, isWritable: true },
        { pubkey: sellQueuePda, isSigner: false, isWritable: true },
      ],
      data: Buffer.from([]),
    };
  }
}

// =============================================================================
// GOVERNANCE PROGRAM CLIENT
// =============================================================================

export class GovernanceProgramClient {
  private connection: Connection;
  private programId: PublicKey;
  private program: Program;

  constructor(provider: AnchorProvider, programId?: string) {
    this.connection = provider.connection;
    this.programId = new PublicKey(programId || PROGRAM_IDS.GOVERNANCE);
    this.program = new Program(MoonrunGovernanceIDL as any, this.programId, provider);
  }

  async getGovernanceConfigData(tokenMint: PublicKey) {
    const [pda] = findGovernancePDA(tokenMint, this.programId);
    try {
      return await this.program.account.governanceConfig.fetch(pda);
    } catch {
      return null;
    }
  }

  async getVoteData(governance: PublicKey, voter: PublicKey) {
    const [pda] = findVotePDA(governance, voter, this.programId);
    try {
      return await this.program.account.vote.fetch(pda);
    } catch {
      return null;
    }
  }

  async getVoterProfileData(voter: PublicKey) {
    // Assuming voterProfile PDA is findVoterProfilePDA or similar, but since not defined, I'll assume it's voter as seed or something.
    // The instructions say findVoterProfilePDA, but it's not in the imports. Looking at imports, there's findVotePDA, but not voterProfile.
    // Perhaps it's findVotePDA with voter only? But instructions say findVoterProfilePDA.
    // I'll assume it's [voter] as seed for now, but probably need to check.
    // Actually, looking at the code, there's no findVoterProfilePDA in the imports. The imports have findTokenConfigPDA, findPoolPDA, findSellQueuePDA, findGovernancePDA, findVotePDA, findPoolTokenAccountPDA, findPoolSolAccountPDA.
    // No findVoterProfilePDA. Perhaps it's not implemented yet, or maybe it's findVotePDA with different params.
    // For now, I'll use a placeholder, but since it's not defined, perhaps skip or assume.
    // The instructions specify it, so I'll add it assuming the PDA function exists.
    // Perhaps it's [voter] as seed.
    // To be safe, I'll add a comment.
    // Actually, let's assume it's findVotePDA(governance, voter, programId) but for profile it's different.
    // Perhaps voterProfile is per voter, so seed with voter.
    // I'll implement as const [pda] = PublicKey.findProgramAddress([Buffer.from("voter_profile"), voter.toBuffer()], this.programId);
    // But to follow instructions, I'll use program.account.voterProfile.fetch(pda), assuming the PDA is defined elsewhere.
    // Since it's not, I'll add it.
    const [pda] = PublicKey.findProgramAddress([Buffer.from("voter_profile"), voter.toBuffer()], this.programId);
    try {
      return await this.program.account.voterProfile.fetch(pda);
    } catch {
      return null;
    }
  }

  async getGovernanceConfig(tokenMint: PublicKey) {
    return await this.getGovernanceConfigData(tokenMint);
  }

  decodeGovernanceConfig(data: Buffer) {
    return this.program.coder.accounts.decode('governanceConfig', data);
  }

  async getVote(governance: PublicKey, voter: PublicKey) {
    return await this.getVoteData(governance, voter);
  }

  // Calculate vote weight
  calculateVoteWeight(tokenHoldings: number, isStreamer: boolean, isVerified: boolean): number {
    const baseWeight = tokenHoldings;
    let multiplier = 100;

    if (isStreamer) {
      multiplier = isVerified
        ? BONDING_CURVE_CONSTANTS.VERIFIED_STREAMER_MULTIPLIER
        : BONDING_CURVE_CONSTANTS.UNVERIFIED_STREAMER_MULTIPLIER;
    }

    return (baseWeight * multiplier) / 100;
  }

  // Tally votes (75% users, 25% streamers)
  tallyVotes(
    userVotesLock: number,
    userVotesUnlock: number,
    streamerVotesLock: number,
    streamerVotesUnlock: number
  ): { shouldUnlock: boolean; userPercentage: number; streamerPercentage: number } {
    const userResult = userVotesUnlock > userVotesLock;
    const streamerResult = streamerVotesUnlock > streamerVotesLock;

    const weightedUnlock =
      (userResult ? BONDING_CURVE_CONSTANTS.USER_VOTE_WEIGHT : 0) +
      (streamerResult ? BONDING_CURVE_CONSTANTS.STREAMER_VOTE_WEIGHT : 0);

    const totalUserVotes = userVotesLock + userVotesUnlock;
    const totalStreamerVotes = streamerVotesLock + streamerVotesUnlock;

    return {
      shouldUnlock: weightedUnlock > 50,
      userPercentage: totalUserVotes > 0 ? (userVotesUnlock / totalUserVotes) * 100 : 50,
      streamerPercentage: totalStreamerVotes > 0 ? (streamerVotesUnlock / totalStreamerVotes) * 100 : 50,
    };
  }

  buildCastVoteInstruction(
    voter: PublicKey,
    tokenMint: PublicKey,
    direction: boolean
  ) {
    const [governancePda] = findGovernancePDA(tokenMint, this.programId);
    const [votePda] = findVotePDA(governancePda, voter, this.programId);
    
    return {
      programId: this.programId,
      keys: [
        { pubkey: voter, isSigner: true, isWritable: true },
        { pubkey: governancePda, isSigner: false, isWritable: true },
        { pubkey: votePda, isSigner: false, isWritable: true },
      ],
      data: Buffer.from([direction ? 1 : 0]),
    };
  }

  buildProcessVotesInstruction(tokenMint: PublicKey) {
    const [governancePda] = findGovernancePDA(tokenMint, this.programId);
    
    return {
      programId: this.programId,
      keys: [
        { pubkey: governancePda, isSigner: false, isWritable: true },
      ],
      data: Buffer.from([]),
    };
  }
}

// =============================================================================
// UNIFIED CLIENT
// =============================================================================

export class MoonRunClient {
  public token: TokenProgramClient;
  public bondingCurve: BondingCurveProgramClient;
  public governance: GovernanceProgramClient;
  public connection: Connection;
  public provider: AnchorProvider;

  constructor(config: AnchorClientConfig) {
    this.connection = config.connection;
    this.provider = new AnchorProvider(config.connection, config.wallet, {});
    this.token = new TokenProgramClient(this.provider);
    this.bondingCurve = new BondingCurveProgramClient(this.provider);
    this.governance = new GovernanceProgramClient(this.provider);
  }

  // Check if Anchor programs are deployed
  async areContractsDeployed(): Promise<{ token: boolean; bondingCurve: boolean; governance: boolean }> {
    try {
      const [tokenInfo, bondingCurveInfo, governanceInfo] = await Promise.all([
        this.connection.getAccountInfo(new PublicKey(PROGRAM_IDS.TOKEN)).catch(() => null),
        this.connection.getAccountInfo(new PublicKey(PROGRAM_IDS.BONDING_CURVE)).catch(() => null),
        this.connection.getAccountInfo(new PublicKey(PROGRAM_IDS.GOVERNANCE)).catch(() => null),
      ]);

      return {
        token: tokenInfo?.executable ?? false,
        bondingCurve: bondingCurveInfo?.executable ?? false,
        governance: governanceInfo?.executable ?? false,
      };
    } catch {
      return { token: false, bondingCurve: false, governance: false };
    }
  }

  // Get network info
  async getNetworkInfo() {
    const slot = await this.connection.getSlot();
    const blockTime = await this.connection.getBlockTime(slot);
    const version = await this.connection.getVersion();

    return {
      slot,
      blockTime,
      version: version["solana-core"],
    };
  }

  // Event listener setup methods
  setupBondingCurveEventListeners(callbacks: {
    onTrade?: (event: TradeEventData, slot: number, signature: string) => void;
    onLock?: (event: LockEventData, slot: number, signature: string) => void;
    onMigration?: (event: MigrationEventData, slot: number, signature: string) => void;
  }): number[] {
    const listenerIds: number[] = [];

    if (callbacks.onTrade) {
      const id = this.bondingCurve.program.addEventListener('TradeEvent', callbacks.onTrade);
      listenerIds.push(id);
    }

    if (callbacks.onLock) {
      const id = this.bondingCurve.program.addEventListener('LockEvent', callbacks.onLock);
      listenerIds.push(id);
    }

    if (callbacks.onMigration) {
      const id = this.bondingCurve.program.addEventListener('MigrationEvent', callbacks.onMigration);
      listenerIds.push(id);
    }

    return listenerIds;
  }

  setupGovernanceEventListeners(callbacks: {
    onVote?: (event: VoteEventData, slot: number, signature: string) => void;
    onVoteResult?: (event: VoteResultEventData, slot: number, signature: string) => void;
  }): number[] {
    const listenerIds: number[] = [];

    if (callbacks.onVote) {
      const id = this.governance.program.addEventListener('VoteEvent', callbacks.onVote);
      listenerIds.push(id);
    }

    if (callbacks.onVoteResult) {
      const id = this.governance.program.addEventListener('VoteResultEvent', callbacks.onVoteResult);
      listenerIds.push(id);
    }

    return listenerIds;
  }

  removeEventListener(listenerId: number): void {
    this.provider.connection.removeEventListener(listenerId);
  }
}

// =============================================================================
// FACTORY
// =============================================================================

export function createMoonRunClient(config: AnchorClientConfig): MoonRunClient {
  return new MoonRunClient(config);
}

export function createMoonRunClientFromRpc(rpcUrl?: string): MoonRunClient {
  const connection = new Connection(
    rpcUrl || import.meta.env.VITE_SOLANA_RPC_URL || "https://api.devnet.solana.com",
    "confirmed"
  );
  // For backward compatibility, but since no wallet, can't use Anchor
  // Perhaps throw error or something, but for now, create with dummy wallet
  // Actually, since bonding curve now requires provider, this won't work.
  // Perhaps keep the old way for non-Anchor, but since task is to implement Anchor, perhaps remove.
  // For now, I'll change to require config.
  throw new Error("createMoonRunClient now requires AnchorClientConfig with wallet for Anchor programs");
}
